import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { styled } from '@linaria/react';
import { zIndex } from '@spaceshipapp/cambridge';
import useAuth from '../../contexts/auth';
import ProVersion from './ProVersion';
import TermsAndConditions, { LOCAL_STORAGE_KEY } from './TermsAndConditions';
import { UserType } from '../../types/auth';
import { setFixedScrollPosition } from '../../utils/helpers';
import * as LocalStorage from '../../utils/localStorage';
import RegionRedirection, { availableCountryList, LOCAL_STORAGE_KEY_IP } from './RegionRedirection';
import getClientCountry from '../../api/internal/getClientCountry';

enum ModalId {
  IP_REDIRECTION = 'IP_REDIRECTION',
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  PRO_VERSION = 'PRO_VERSION',
}

const IPDETECTION_ENABLED = process.env.NEXT_PUBLIC_IPDETECTION_ENABLED === 'TRUE';

const GlobalModal: React.FC = () => {
  const [{ user }] = useAuth();
  const { userType } = user || {};

  const [openedModals, setOpenedModals] = useState<ModalId[]>([]);
  const [countryCode, setCountryCode] = useState<string>();
  const [modalToShow] = openedModals;

  const handleClose = (modalId: ModalId) => {
    const remain = openedModals.filter((modal) => modal !== modalId);
    setOpenedModals(remain);
  };

  const ref = useCallback((node) => {
    const isOpen = !!node;
    setFixedScrollPosition(isOpen);
  }, []);

  useEffect(() => {
    if (LocalStorage.getItem(LOCAL_STORAGE_KEY_IP)) {
      setCountryCode(process.env.NEXT_PUBLIC_COUNTRY);
      return;
    }
    const clientIpRequest = async () => {
      const countryCode = await getClientCountry();
      const isAvailableCountry =
        availableCountryList.includes(countryCode) &&
        process.env.NEXT_PUBLIC_COUNTRY !== countryCode;
      const showIpRedirection = isAvailableCountry;
      if (showIpRedirection) {
        setOpenedModals([...openedModals, ModalId.IP_REDIRECTION]);
      }
      setCountryCode(countryCode);
    };
    if (IPDETECTION_ENABLED) clientIpRequest();
  }, []);

  useEffect(() => {
    // only show dialog for business users
    if (userType === UserType.BUSINESS) {
      setOpenedModals([...openedModals, ModalId.PRO_VERSION]);
    }
  }, [userType]);

  useEffect(() => {
    const isDismissed = LocalStorage.getItem(LOCAL_STORAGE_KEY);
    if (!isDismissed && countryCode) {
      setOpenedModals([...openedModals, ModalId.TERMS_AND_CONDITIONS]);
    }
  }, [countryCode]);

  if (modalToShow === ModalId.IP_REDIRECTION) {
    return (
      <Fragment>
        <Overlay ref={ref} />
        <RegionRedirection
          countryCode={countryCode}
          onClose={() => handleClose(ModalId.IP_REDIRECTION)}
        />
      </Fragment>
    );
  }

  if (modalToShow === ModalId.TERMS_AND_CONDITIONS) {
    return (
      <Fragment>
        <Overlay ref={ref} />
        <TermsAndConditions onClose={() => handleClose(ModalId.TERMS_AND_CONDITIONS)} />
      </Fragment>
    );
  }

  if (modalToShow === ModalId.PRO_VERSION) {
    return (
      <Fragment>
        <Overlay ref={ref} />
        <ProVersion onClose={() => handleClose(ModalId.PRO_VERSION)} />
      </Fragment>
    );
  }

  return null;
};

const Overlay = styled.div`
  position: fixed;
  z-index: ${zIndex.MODAL};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(34, 34, 34, 0.3);
`;

export default GlobalModal;
